import React, {
  ChangeEvent,
  forwardRef,
  RefObject,
  useEffect,
  useRef,
} from 'react';
import { IconCalendarOutline } from '@iwoca/icons';
import { Button, Input } from '@iwoca/orion';
import styles from './DateInput.module.css';

type TProps = {
  toggleCalendar: (isOpen: boolean) => void;
  handleChange: (newDate: Date) => void;
  selectedDate: Date | null;
  setSelectedDate: (value: React.SetStateAction<Date | null>) => void;
  defaultDate?: string;
  calendarDate?: Date | null;
  filterDate?: (date: Date) => boolean;
  dateInputRef?: RefObject<any>;
};

export const DateInput = forwardRef<HTMLButtonElement, TProps>(
  (
    {
      toggleCalendar,
      handleChange,
      selectedDate,
      setSelectedDate,
      defaultDate,
      calendarDate,
      filterDate,
      dateInputRef,
    },
    ref,
  ) => {
    const defaultInputRef = useRef<HTMLInputElement>(null);
    const inputRef = dateInputRef || defaultInputRef;
    const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
      const dateAsString = e.target.value;
      const updatedDate = new Date(dateAsString);

      if (
        !isNaN(updatedDate.getTime()) &&
        (!filterDate || filterDate(updatedDate))
      ) {
        handleChange(updatedDate);
        setSelectedDate(updatedDate);
      } else if (dateAsString === '' && selectedDate) {
        setSelectedDate(null);
      }
    };

    useEffect(() => {
      if (calendarDate && inputRef.current) {
        inputRef.current.value = calendarDate.toISOString().split('T')[0];
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarDate]);

    return (
      <div className={styles.dateInputContainer}>
        <Input
          ref={inputRef}
          className={styles.dateInput}
          wrapperClassName={styles.wrapper}
          type="date"
          data-testid="data-picker-input"
          defaultValue={defaultDate}
          onChange={handleDateChange}
        />
        <div className={styles.buttonContainer}>
          <Button
            className={styles.dateInputButton}
            variant="tertiary"
            onClick={() => toggleCalendar(true)}
            data-testid="data-picker-input-button"
            ref={ref}
          >
            <IconCalendarOutline />
          </Button>
        </div>
      </div>
    );
  },
);

DateInput.displayName = 'DateInput';
